import React, { useEffect, useState } from 'react';
import UnauthenticatedPageContainer from '../auth/UnauthenticatedPageContainer';
import { useParams } from 'react-router';
import PaddedContent from '../../components/housing/PaddedContent';
import FormDetails from '../forms/FormDetails';
import { Button } from '../../components/common';
import { useMessageGetter } from 'react-message-context';
import { ButtonContainer } from '../layoutComponents';
import styled from 'styled-components/macro';
import InfoBox from '../../components/common/InfoBox';
import { NotificationType, useAppDispatch } from '../../context/appContext';
import Loader from '../../components/basic/Loader';
import { approveFormByToken, getFormByToken, rejectFormByToken, resendToken } from '../../apis_kenno';
import { showNotification } from '../../context/appActions';
import { isApproved, isRejected, isWaitingForApproval } from '../forms/form-utils';
import { scrollToTop } from '../../utils';

const PaddedContainer = styled(PaddedContent)`
  margin-top: 0;
`;

const Container = styled.div`
  position: relative;
  border-radius: 40px;
  z-index: 1;
  margin-bottom: 30px;
`;

const enum ReviewDecision {
  APPROVE = 'approve',
  REJECT = 'reject',
}

const enum TokenError {
  EXPIRED = 'expired',
  NOT_FOUND = 'not_found',
  GENERAL = 'general',
}

const FormReviewPage: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const appDispatch = useAppDispatch();
  const t = useMessageGetter('forms.review');
  const tForms = useMessageGetter('forms');
  const [loading, setLoading] = useState<boolean>(true);
  const [tokenError, setTokenError] = useState<TokenError | null>(null);
  const [form, setForm] = useState<any>(null);
  const [reviewDecision, setReviewDecision] = useState<ReviewDecision | null>(null);

  useEffect(() => {
    const fetchForm = async () => {
      setTokenError(null);
      try {
        if (token) {
          const fetchedForm = await getFormByToken(token);
          setForm(fetchedForm);
          // Form will be visible for admins x days from the approval/rejection
          // (time period can be adjusted in the backend).
          if (!isWaitingForApproval(fetchedForm)) return;

          if (isApproved(fetchedForm)) {
            setReviewDecision(ReviewDecision.APPROVE);
          } else if (isRejected(fetchedForm)) {
            setReviewDecision(ReviewDecision.REJECT);
          }
        }
      } catch (e: any) {
        console.error(e);
        if (e.response?.status === 401) {
          setTokenError(TokenError.EXPIRED);
        } else if (e.response?.status === 404) {
          setTokenError(TokenError.NOT_FOUND);
        } else {
          setTokenError(TokenError.GENERAL);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [token]);

  const renderReviewDecision = () => {
    const messageKey = reviewDecision === ReviewDecision.APPROVE ? 'formApproved' : 'formRejected';
    const notificationType = reviewDecision === ReviewDecision.APPROVE ? NotificationType.INFO : NotificationType.ERROR;
    return <InfoBox type={notificationType} message={t(messageKey)} />;
  };

  const renderForm = () => {
    return (
      <>
        {reviewDecision && renderReviewDecision()}
        <FormDetails form={form}>
          {isWaitingForApproval(form) && !reviewDecision && (
            <ButtonContainer>
              <Button icon="checkMark" primary onClick={() => handleFormReview(ReviewDecision.APPROVE)}>
                {tForms('approve')}
              </Button>
              <Button icon="xClose" onClick={() => handleFormReview(ReviewDecision.REJECT)}>
                {tForms('reject')}
              </Button>
            </ButtonContainer>
          )}
        </FormDetails>
      </>
    );
  };

  const renderFormError = () => {
    if (tokenError === TokenError.EXPIRED) {
      return (
        <InfoBox type={NotificationType.ERROR} message={t('tokenExpired')} button={NewTokenButton} width={'400px'} />
      );
    } else if (tokenError === TokenError.NOT_FOUND) {
      return <InfoBox type={NotificationType.ERROR} message={t('tokenNotFound')} />;
    } else if (tokenError === TokenError.GENERAL) {
      return <InfoBox type={NotificationType.ERROR} message={t('error')} />;
    } else {
      return null;
    }
  };

  const NewTokenButton = (
    <Button primary size="medium" onClick={() => handleNewToken()}>
      {t('orderNewToken')}
    </Button>
  );

  const handleNewToken = async () => {
    try {
      await resendToken(token!);
      await showNotification(appDispatch, t('newTokenSent'), NotificationType.SUCCESS);
    } catch (e) {
      await showNotification(appDispatch, t('error'), NotificationType.ERROR);
    }
  };

  const handleFormReview = async (decision: ReviewDecision) => {
    try {
      setLoading(true);
      if (token) {
        if (decision === ReviewDecision.APPROVE) {
          await approveFormByToken(token);
        } else if (decision === ReviewDecision.REJECT) {
          await rejectFormByToken(token);
        }
        setReviewDecision(decision);
        scrollToTop();
      }
    } catch (e) {
      console.error(e);
      await showNotification(appDispatch, t('error'), NotificationType.ERROR);
    } finally {
      setLoading(false);
    }
  };

  return (
    <UnauthenticatedPageContainer>
      <PaddedContainer paddingTop="0" mobilePaddingTop="0">
        <Container>{loading ? <Loader /> : form ? renderForm() : renderFormError()}</Container>
      </PaddedContainer>
    </UnauthenticatedPageContainer>
  );
};

export default FormReviewPage;
